/**
 * Which list a role can carry a default saved view / filter for. The values
 * are the backend's surface vocabulary (`EventRoleTemplateService.SURFACES`)
 * AND the frontend's `ViewFilterType` for those lists, so nothing is mapped on
 * the way to `fetchViewsList` / `fetchFiltersList`.
 */
export const ROLE_DEFAULT_SURFACES = ["guest", "registrant", "companion"] as const;

export type RoleDefaultSurface = (typeof ROLE_DEFAULT_SURFACES)[number];

/**
 * One list's default view/filter for a role (EventRoleTemplate.surfaceDefaults).
 *
 * Guest-scoped views and filters are EVENT-shared, not per user, so this is a
 * pointer and never a copy: every collaborator on the event can already load
 * the row it names. The cockpit applies it only when the user has no stored
 * preference for that list yet — a starting point, never an enforcement (row
 * filters and the field allowlist are what enforce).
 *
 * Ids are not resolved server-side: a deleted, foreign or wrong-surface id
 * simply falls back to the list's DEFAULT view.
 */
export interface RoleSurfaceDefault {
  surface: RoleDefaultSurface;
  /** Saved view id, or absent for "the DEFAULT view". */
  tableViewId?: string;
  /** Saved filter id, or absent for "no filter". */
  tableFilterId?: string;
  /**
   * Server-supplied on the RESOLVED envelope only, and only on entries that
   * came from the collaborator's own row: the ISO instant of the admin write
   * that produced them. Entries inherited from the role template have none.
   *
   * The cockpit re-applies a CHANGED default once, over the user's own pick —
   * so it has to know which entries an admin actually just wrote. A list-level
   * timestamp cannot say that, because the list is a merge of two sources.
   * Never sent back on a write; the server rebuilds every entry it stores.
   */
  setAt?: string;
}
